import React from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, Download, Check, Github } from 'lucide-react';

/**
 * Free version on WordPress.org — promo section.
 * Highlights the free distribution channel, the open-source nature,
 * and clarifies what's free vs Pro.
 */
const FreeWordPressOrgSection = () => {
  const { t } = useTranslation();

  const includedFree = [
    t('freeWp.feat.llms'),
    t('freeWp.feat.head'),
    t('freeWp.feat.sitemap'),
    t('freeWp.feat.schema'),
    t('freeWp.feat.redirect'),
    t('freeWp.feat.migration'),
  ];

  const proOnly = [
    t('freeWp.pro.brand'),
    t('freeWp.pro.autopilot'),
    t('freeWp.pro.keyword'),
    t('freeWp.pro.cannibal'),
    t('freeWp.pro.gsc'),
    t('freeWp.pro.image'),
  ];

  return (
    <section className="py-20 px-4 bg-gradient-to-br from-blue-50 via-white to-cyan-50 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 rounded-full bg-blue-600 text-white text-sm font-semibold">
            <Heart className="w-4 h-4" />
            {t('freeWp.badge')}
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-slate-900 dark:text-white">
            {t('freeWp.title')}
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            {t('freeWp.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {/* Free side */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg border-2 border-blue-200 dark:border-blue-700">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
                <Download className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">{t('freeWp.freeTitle')}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t('freeWp.freePrice')}</p>
              </div>
            </div>
            <ul className="space-y-3 mb-6">
              {includedFree.map((feat, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 dark:text-slate-200">{feat}</span>
                </li>
              ))}
            </ul>
            <a
              href="https://wordpress.org/plugins/aeo-orchestra/"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center py-3 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition"
            >
              {t('freeWp.ctaWp')} →
            </a>
            <a
              href="https://github.com/jcappelli/aeo-orchestra"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 mt-3 text-sm text-slate-500 hover:text-blue-600 dark:text-slate-400"
            >
              <Github className="w-4 h-4" />
              {t('freeWp.ctaGithub')}
            </a>
          </div>

          {/* Pro side */}
          <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-2xl p-8 shadow-xl text-white">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-cyan-400 flex items-center justify-center">
                <span className="text-2xl">💎</span>
              </div>
              <div>
                <h3 className="text-xl font-bold">{t('freeWp.proTitle')}</h3>
                <p className="text-sm text-cyan-200">{t('freeWp.proPrice')}</p>
              </div>
            </div>
            <ul className="space-y-3 mb-6">
              {proOnly.map((feat, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check className="w-5 h-5 text-cyan-300 flex-shrink-0 mt-0.5" />
                  <span className="text-blue-100">{feat}</span>
                </li>
              ))}
            </ul>
            <a
              href="#pricing"
              className="block w-full text-center py-3 px-6 rounded-xl bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold transition"
            >
              {t('freeWp.ctaPro')} →
            </a>
          </div>
        </div>

        <div className="text-center text-sm text-slate-500 dark:text-slate-400">
          {t('freeWp.coexistNote')}
        </div>
      </div>
    </section>
  );
};

export default FreeWordPressOrgSection;
